import React, { useState, useEffect } from "react";
import {
  Stack,
  Group,
  Text,
  ActionIcon,
  TextInput,
  Button,
  SimpleGrid,
  Paper,
  Box,
  Image,
  Switch,
} from "@mantine/core";
import { IconChevronLeft, IconPhoto, IconCheck } from "@tabler/icons-react";
import WebsiteGeneratorPhotoGallery from "./WebsiteGeneratorPhotoGallery";
import CustomRichTextEditor from "../../common/RichTextEditor";
import {
  hasFieldError as checkFieldError,
  getInputStyles,
  getLabelStyles,
} from "../utils/validation";

const COLOR_BORDER_SELECTED = "#1976d2";
const COLOR_BORDER_LIGHT = "#e9ecef";

interface HeroContent {
  title?: string;
  description?: string;
  text?: string; // Rich text field (replaces body)
  photos?: Array<{ url?: string; src?: string; filename?: string }>;
  includeBookingButton?: boolean;
  heroBookingCopy?: string;
}

interface ImageData {
  id?: string | number;
  url?: string;
  src?: string;
  filename?: string;
  [key: string]: any;
}

interface WebsiteGeneratorHeroContentProps {
  heroContent?: HeroContent;
  onContentChange: (content: HeroContent) => void;
  heroImageStateArray?: ImageData[];
  onImageStateChange?: (images: ImageData[]) => void;
  selectedHeroPhoto?: ImageData | null;
  onPhotoSelect: (photo: ImageData) => void;
  onBack: () => void;
  // Global booking link state handlers
  bookingLinkContent?: {
    copy?: string;
    useCustomLink?: boolean;
    link?: string;
  };
  onBookingLinkContentChange?: (content: {
    copy?: string;
    useCustomLink?: boolean;
    link?: string;
  }) => void;
  missingFields?: string[];
  // Validation prop for field-level highlighting
  requiredValidation?: { [field: string]: boolean };
}

const WebsiteGeneratorHeroContent: React.FC<
  WebsiteGeneratorHeroContentProps
> = ({
  heroContent,
  onContentChange,
  heroImageStateArray,
  onImageStateChange,
  selectedHeroPhoto,
  onPhotoSelect,
  onBack,
  bookingLinkContent,
  onBookingLinkContentChange,
  missingFields = [],
  requiredValidation = {},
}) => {
  // Use standardized validation helper (pass value so errors clear when filled)
  const hasFieldError = (fieldName: string, value?: any): boolean => {
    return checkFieldError(fieldName, requiredValidation, value);
  };

  const [isPhotoGalleryOpen, setIsPhotoGalleryOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Simple state for text (rich text field)
  const [bodyText, setBodyText] = useState(heroContent?.text || "");

  // Update local state when heroContent changes
  useEffect(() => {
    setBodyText(heroContent?.text || "");
  }, [heroContent?.text]);

  // Handle input changes
  const handleInputChange = (field: keyof HeroContent, value: string) => {
    // Validate character limit
    if (value.length > 100) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: `${field} cannot exceed 100 characters`,
      }));
    } else {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Immediate update
    onContentChange({
      ...heroContent,
      [field]: value,
    });
  };

  // Handle text (rich text) changes
  const handleBodyChange = (value: string) => {
    setBodyText(value);

    // Validate word count
    const wordCount = value
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
    if (wordCount > 250) {
      setValidationErrors((prev) => ({
        ...prev,
        text: "Text cannot exceed 250 words",
      }));
    } else {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.text;
        return newErrors;
      });
    }

    // Immediate update
    onContentChange({
      ...heroContent,
      text: value,
    });
  };

  const handlePhotoGalleryOpen = () => {
    setIsPhotoGalleryOpen(true);
  };

  const handlePhotoGalleryClose = () => {
    setIsPhotoGalleryOpen(false);
  };

  const handleImageSelect = (images: ImageData[]) => {
    if (onImageStateChange) {
      onImageStateChange(images);
    }
  };

  const handlePhotoSelect = (photo: ImageData) => {
    onPhotoSelect(photo);
  };

  const handleToggleIncludeBooking = (checked: boolean) => {
    onContentChange({
      ...heroContent,
      includeBookingButton: checked,
      heroBookingCopy: checked
        ? (heroContent?.heroBookingCopy ?? "Book Now")
        : heroContent?.heroBookingCopy,
    });
  };

  const handleHeroBookingCopyChange = (value: string) => {
    if (value.length > 100) {
      setValidationErrors((prev) => ({
        ...prev,
        heroBookingCopy: "heroBookingCopy cannot exceed 100 characters",
      }));
      return;
    }
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.heroBookingCopy;
      return next;
    });
    onContentChange({ ...heroContent, heroBookingCopy: value });
  };

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between" align="center">
        <ActionIcon variant="subtle" onClick={onBack}>
          <IconChevronLeft size={16} />
        </ActionIcon>
        <Text fw={600} size="lg">
          Hero
        </Text>
      </Group>

      {/* Section Title */}
      <Box>
        <Text
          size="sm"
          fw={500}
          mb="xs"
          style={getLabelStyles(
            "title",
            requiredValidation,
            heroContent?.title,
          )}
        >
          Section Title {hasFieldError("title", heroContent?.title) && "*"}
        </Text>
        <TextInput
          value={heroContent?.title || ""}
          onChange={(e) => handleInputChange("title", e.target.value)}
          placeholder="Section Title"
          error={
            validationErrors.title ||
            (hasFieldError("title", heroContent?.title)
              ? "Required"
              : undefined)
          }
          maxLength={100}
          styles={getInputStyles(
            "title",
            requiredValidation,
            missingFields,
            "hero_section.header",
          )}
        />
        <Text size="xs" c="dimmed" mt="xs">
          {heroContent?.title?.length || 0}/100 characters
        </Text>
      </Box>

      {/* Section Description */}
      <Box>
        <Text
          size="sm"
          fw={500}
          mb="xs"
          style={getLabelStyles(
            "description",
            requiredValidation,
            heroContent?.description,
          )}
        >
          Section Description{" "}
          {hasFieldError("description", heroContent?.description) && "*"}
        </Text>
        <TextInput
          value={heroContent?.description || ""}
          onChange={(e) => handleInputChange("description", e.target.value)}
          placeholder="Section Description"
          error={
            validationErrors.description ||
            (hasFieldError("description", heroContent?.description)
              ? "Required"
              : undefined)
          }
          maxLength={100}
          styles={getInputStyles(
            "description",
            requiredValidation,
            missingFields,
            "hero_section.subhead",
          )}
        />
        <Text size="xs" c="dimmed" mt="xs">
          {heroContent?.description?.length || 0}/100 characters
        </Text>
      </Box>

      {/* Section Body */}
      <Box>
        <CustomRichTextEditor
          label="Section Body"
          placeholder="Enter your content here..."
          value={bodyText}
          onChange={handleBodyChange}
          minHeight={200}
        />
        {validationErrors.text && (
          <Text size="xs" c="red" mt="xs">
            {validationErrors.text}
          </Text>
        )}
        <Text size="xs" c="dimmed" mt="xs">
          {bodyText.split(/\s+/).filter((word) => word.length > 0).length}/250
          words
        </Text>
      </Box>

      {/* Booking Button (optional) */}
      <Box>
        <Group justify="space-between" align="flex-start">
          <Box style={{ flex: 1 }}>
            <Text size="sm" fw={500} mb="xs">
              Include Booking Button
            </Text>
            <Text size="xs" c="dimmed">
              Add a call-to-action button to the hero section
            </Text>
          </Box>
          <Switch
            checked={!!heroContent?.includeBookingButton}
            onChange={(e) =>
              handleToggleIncludeBooking(e.currentTarget.checked)
            }
            style={{ marginTop: "4px" }}
          />
        </Group>

        {heroContent?.includeBookingButton && (
          <Stack gap="sm" mt="sm">
            <Box>
              <Text
                size="sm"
                fw={500}
                mb="xs"
                style={getLabelStyles(
                  "heroBookingCopy",
                  requiredValidation,
                  heroContent?.heroBookingCopy ?? "Book Now",
                )}
              >
                Booking Link Copy{" "}
                {hasFieldError(
                  "heroBookingCopy",
                  heroContent?.heroBookingCopy ?? "Book Now",
                ) && "*"}
              </Text>
              <TextInput
                placeholder="Book Now"
                value={heroContent?.heroBookingCopy ?? "Book Now"}
                onChange={(e) =>
                  handleHeroBookingCopyChange(e.currentTarget.value)
                }
                error={
                  validationErrors.heroBookingCopy ||
                  (hasFieldError(
                    "heroBookingCopy",
                    heroContent?.heroBookingCopy ?? "Book Now",
                  )
                    ? "Required"
                    : undefined)
                }
                maxLength={100}
                styles={getInputStyles(
                  "heroBookingCopy",
                  requiredValidation,
                  missingFields,
                  "hero_section.book_link_copy",
                )}
              />
              <Text size="xs" c="dimmed" mt="xs">
                Uses the same booking link as the Booking Link section
              </Text>
            </Box>

            <Box>
              <Text size="sm" fw={500} mb="xs">
                Global Booking Link URL
              </Text>
              <TextInput
                placeholder={bookingLinkContent?.link || "https://..."}
                value={
                  bookingLinkContent?.useCustomLink
                    ? bookingLinkContent?.link || ""
                    : bookingLinkContent?.link || ""
                }
                onChange={(e) =>
                  onBookingLinkContentChange &&
                  onBookingLinkContentChange({
                    useCustomLink: true,
                    link: e.currentTarget.value,
                  })
                }
                maxLength={300}
              />
              <Text size="xs" c="dimmed" mt="xs">
                This updates the global booking link URL.
              </Text>
            </Box>
          </Stack>
        )}
      </Box>

      {/* Section Photo */}
      <Box>
        <Text
          size="sm"
          fw={500}
          mb="xs"
          style={getLabelStyles("image", requiredValidation, selectedHeroPhoto)}
        >
          Section Photo {hasFieldError("image", selectedHeroPhoto) && "*"}
        </Text>

        {/* Photo Selection Area */}
        {heroImageStateArray && heroImageStateArray.length > 0 && (
          <SimpleGrid cols={3} spacing="sm" mb="md">
            {heroImageStateArray.map((image, index) => (
              <Paper
                key={index}
                p="sm"
                style={{
                  border:
                    selectedHeroPhoto?.id === image.id
                      ? `3px solid ${COLOR_BORDER_SELECTED}`
                      : `1px solid ${COLOR_BORDER_LIGHT}`,
                  borderRadius: "8px",
                  cursor: "pointer",
                  position: "relative",
                }}
                onClick={() => handlePhotoSelect(image)}
              >
                {selectedHeroPhoto?.id === image.id && (
                  <ActionIcon
                    variant="filled"
                    color="blue"
                    size="sm"
                    style={{
                      position: "absolute",
                      top: "4px",
                      left: "4px",
                      zIndex: 2,
                    }}
                  >
                    <IconCheck size={12} />
                  </ActionIcon>
                )}
                <Image
                  src={image.url || image.src}
                  alt={image.filename || "Image"}
                  style={{
                    width: "100%",
                    height: "80px",
                    objectFit: "cover",
                    borderRadius: "4px",
                  }}
                />
                <Text size="xs" c="dimmed" mt="xs" truncate>
                  {image.filename || "image.jpg"}
                </Text>
              </Paper>
            ))}
          </SimpleGrid>
        )}

        <Button
          variant="outline"
          leftSection={<IconPhoto size={16} />}
          onClick={handlePhotoGalleryOpen}
        >
          Select Photo
        </Button>
      </Box>

      {/* Photo Gallery Modal */}
      <WebsiteGeneratorPhotoGallery
        isOpen={isPhotoGalleryOpen}
        onClose={handlePhotoGalleryClose}
        images={heroImageStateArray}
        selectedImages={heroImageStateArray}
        onImageSelect={handleImageSelect}
        onImageDelete={(image) => {
          if (onImageStateChange && heroImageStateArray) {
            const updatedImages = heroImageStateArray.filter(
              (img) => img.id !== image.id,
            );
            onImageStateChange(updatedImages);
          }
        }}
        onImageEdit={(image) => {
          // TODO: Implement image edit functionality
        }}
        onConfirmSelection={() => {
          // Selection is already handled by onImageSelect
        }}
      />
    </Stack>
  );
};

export default WebsiteGeneratorHeroContent;
